import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { BottomMenuSettings } from "@42maru/webchat";
import { LabelKeyValueItem, ButtomMenuButton } from "@/model";
import { withArray } from "@/util/arrayUtil";
import { getLabelKeyFromValue } from "@/util/typeUtil";
import { isValidButtonData } from "@/util/helper/webchatThemeHelper";
import { useUploadedFiles, useSyncForm } from "@/hook";
import { ParagraphButtonTypeDropdown, ParagraphButtonInputs } from "@/component/paragraphEditor";
import { DropdownGroup, DropdownItem, DropdownItems, DropdownButton, ImageUploadBtn, SwitchInput } from "@/component/input";
import ClickableImg from "@/component/ClickableImg";
import { DeleteIcon } from "@/assets/svgs/icons";


const LAYOUTS = [[1, 2], [1, 3], [2, 3]] as const;
type LayoutType = typeof LAYOUTS[number];

interface ChatQuickMenuInputGroupProps {
    disabled?: boolean;
    quickMenu: BottomMenuSettings;
    legend: string;
    onChange: (quickMenu: BottomMenuSettings) => void;
}

const ChatQuickMenuInputGroup: React.FC<ChatQuickMenuInputGroupProps> = (props) => {
    const { quickMenu, disabled = false, legend, onChange } = props;

    const intl = useIntl();

    const [selectedLayout, setSelectedLayout] = useState<LayoutType>();
    const [selectedBtnIdx, setSelectedBtnIdx] = useState<number>(0);
    const [buttons, setButtons] = useState<ButtomMenuButton[]>(
        Array(6).fill(0).map((_, index) => {
            const btn = quickMenu?.data?.[index];
            return btn
                ? structuredClone(btn)
                : {
                    button_type: "text",
                    label: "",
                    image_type: "icon",
                    image_url: ""
                };
            }
        )
    );

    const onChangeLayout = (layout: LayoutType) => {
        setSelectedLayout(layout);

        const btnLength = layout[0] * layout[1];
        if (selectedBtnIdx >= btnLength) {
            setSelectedBtnIdx(0);
        }
        onChange({
            ...quickMenu,
            line: layout[0],
            data: buttons.slice(0, btnLength)
        });
    };

    const onChangeButton = (button: ButtomMenuButton) => {
        const newButtons = withArray(buttons, selectedBtnIdx, button);
        setButtons(newButtons);
        onChange({
            ...quickMenu,
            data: newButtons.slice(0, selectedLayout ? selectedLayout[0] * selectedLayout[1] : 6)
        });
    };

    useEffect(() => {
        const line = quickMenu?.line;
        const btnCount = quickMenu?.data?.length ?? 0;
        if (!line || !btnCount || btnCount % line !== 0) {
            return;
        }
        const row = Math.floor(btnCount / line);
        const layout = LAYOUTS.find(layout => layout[0] === line && layout[1] === row);
        if (layout) {
            setSelectedLayout(layout);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const syncFromProps = useCallback(() => {
        if (!quickMenu.data) {
            return;
        }
        setButtons(prev => prev.map((btn, idx) => structuredClone(quickMenu.data[idx] ?? btn)));
    }, [quickMenu.data]);

    useSyncForm(syncFromProps);

    return (
        <fieldset className="webchat-setting-card-grid ml-4 gap-y-6">
            <legend className="sr-only">
                {legend}
            </legend>
            <div className="col-span-2 flex flex-col">
                <div className={classNames("field-label", disabled && "field-label-disabled")}>
                    {intl.formatMessage({ id: "i09091" })}
                </div>
                <QuickMenuLayoutSelector className="mt-2"
                                         disabled={disabled}
                                         selectedLayout={selectedLayout}
                                         selectedBtnIdx={selectedBtnIdx}
                                         buttons={buttons}
                                         onChangeLayout={onChangeLayout}
                                         onClickButtonLayout={setSelectedBtnIdx}
                />
                <div className="mt-6 flex flex-col gap-1.5">
                    {buttons[selectedBtnIdx] && (
                        <ButtonFragment button={buttons[selectedBtnIdx]}
                                        disabled={disabled || selectedLayout === undefined || selectedBtnIdx === undefined}
                                        onChange={onChangeButton}
                        />
                    )}
                </div>
            </div>

            <label htmlFor="webchat-quick-menu-open"
                   className={classNames("field-label", disabled && "field-label-disabled")}
            >
                {intl.formatMessage({ id: "i09092" })}
            </label>
            <SwitchInput id="webchat-quick-menu-open"
                         disabled={disabled}
                         value={quickMenu.open ?? false}
                         onSwitch={checked => onChange({ ...quickMenu, open: checked })}
            />

            <label htmlFor="webchat-quick-menu-dim"
                   className={classNames("-mt-2 field-label", disabled && "field-label-disabled")}
            >
                {intl.formatMessage({ id: "i09093" })}
            </label>
            <SwitchInput id="webchat-quick-menu-dim"
                         disabled={disabled}
                         className="-mt-2"
                         value={quickMenu.dim ?? false}
                         onSwitch={checked => onChange({ ...quickMenu, dim: checked })}
                />
        </fieldset>
    );
};

interface QuickMenuLayoutSelectorProps {
    className?: string;
    selectedLayout: LayoutType | undefined;
    disabled: boolean;
    selectedBtnIdx: number;
    buttons: ButtomMenuButton[];
    onChangeLayout: (layout: LayoutType) => void;
    onClickButtonLayout: (index: number) => void;
}

const QuickMenuLayoutSelector: React.FC<QuickMenuLayoutSelectorProps> = (props) => {
    const { className, selectedLayout, disabled, selectedBtnIdx, buttons, onChangeLayout, onClickButtonLayout } = props;

    const intl = useIntl();

    const getButtonClassNames = useCallback((layout: LayoutType, index: number) => {
        const isDisabled = disabled || selectedLayout !== layout;
        const isSelectedBtn = selectedBtnIdx === index;
        const isValid = isValidButtonData(buttons[index]);
        return classNames(
            "w-[48px] h-[25px] rounded-[4px]",
            isDisabled && "bg-black-200",
            !isDisabled && "bg-chat42-10",
            !isDisabled && isSelectedBtn && "border !border-chat42",
            !isDisabled && !isValid && "border border-point-red"
        );
    }, [disabled, selectedLayout, selectedBtnIdx, buttons]);

    return (
        <fieldset className={classNames("flex flex-wrap gap-8", className)}>
            <legend className="sr-only">
                {intl.formatMessage({ id: "i09091" })}
            </legend>
            {LAYOUTS.map(layout => {
                const isSelectedLayout = selectedLayout === layout;
                const layoutLabel = `${layout[0]}x${layout[1]}`;
                return (
                    <div key={layoutLabel}
                         className="flex flex-col gap-2"
                    >
                        <label className={classNames(
                                       "w-full flex gap-1.5 items-center",
                                       !disabled ? "cursor-pointer" : "pointer-events-none"
                                   )}
                        >
                            <input type="radio"
                                   name="chat-quick-menu-layout"
                                   className="input-radio-chat42 size-4"
                                   disabled={disabled}
                                   checked={isSelectedLayout}
                                   onChange={() => onChangeLayout(layout)}
                            />
                            <div className="input-label">
                                {layoutLabel}
                            </div>
                        </label>

                        <div role="group"
                             aria-label={layoutLabel}
                             className="grid grid-rows-[repeat(2,auto)] gap-0.5"
                             style={{ gridTemplateColumns: `repeat(${layout[1]}, 1fr)` }}
                        >
                            {Array(layout[0] * layout[1]).fill(0).map((_, index) => (
                                <button key={index}
                                        disabled={disabled || !isSelectedLayout}
                                        className={getButtonClassNames(layout, index)}
                                        onClick={() => onClickButtonLayout(index)}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </fieldset>
    );
};

type ButtonShowType = ButtomMenuButton["image_type"] | "text";
const BUTTON_TYPE_OPTIONS = ["text", "next", "link", "scenario"] as ButtomMenuButton["button_type"][];

const BUTTON_SHOW_OPTIONS: LabelKeyValueItem<ButtonShowType>[] = [
    { value: "icon", labelKey: "i09098" },
    { value: "image", labelKey: "i09099" },
    { value: "text", labelKey: "i09097" }
];

interface ButtonFragmentProps {
    button: ButtomMenuButton;
    disabled?: boolean;
    onChange: (button: ButtomMenuButton) => void;
}

const ButtonFragment: React.FC<ButtonFragmentProps> = (props) => {
    const { button, disabled = false, onChange } = props;

    const intl = useIntl();
    const { add, remove } = useUploadedFiles();

    const [buttonType, setButtonType] = useState<ButtonShowType>(button.image_type ?? "icon");

    const onChangeButtonType = (buttonType: ButtonShowType) => {
        setButtonType(buttonType);
        switch (buttonType) {
            case "text":
                onChange({ ...button, image_type: "icon", image_url: "" });
                break;
            case "icon":
                onChange({ ...button, image_type: "icon" });
                break;
            case "image":
                onChange({ ...button, image_type: "image", label: "" });
                break;
        }
    };

    const onUploadImage = async (file: File, url: string) => {
        if (button.image_url) {
            remove(button.image_url);
        }
        add(file, url);
        onChange({ ...button, image_url: url });
    };

    const onDeleteImage = () => {
        if (button.image_url) {
            remove(button.image_url);
        }
        onChange({ ...button, image_url: "" });
    };

    //TODO text 타입이 새로 추가될 예정. 그 전까지 임시 ui 테스트용 코드
    useEffect(() => {
        if (button.image_type === "icon" && !button.image_url && button.label?.trim()) {
            setButtonType("text");
        } else {
            setButtonType(button.image_type);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
                <DropdownGroup width={120}>{({ open, close }) => (<>
                    <DropdownButton open={open} close={close} disabled={disabled}>
                        {intl.formatMessage({ id: getLabelKeyFromValue(BUTTON_SHOW_OPTIONS, buttonType) })}
                    </DropdownButton>
                    <DropdownItems>
                        {BUTTON_SHOW_OPTIONS.map((option, index) => (
                            <DropdownItem key={option.value}
                                          isOdd={index % 2 === 1}
                                          onClick={() => onChangeButtonType(option.value)}
                            >
                                {intl.formatMessage({ id: option.labelKey })}
                            </DropdownItem>
                        ))}
                    </DropdownItems>
                </>)}</DropdownGroup>

                <input type="text"
                       aria-label={intl.formatMessage({ id: "i04070" })}
                       disabled={disabled || buttonType === "image"}
                       className="input-default flex-1"
                       placeholder={intl.formatMessage({ id: "i04070" })}
                       value={button.label}
                       onChange={e => onChange({ ...button, label: e.target.value })}
                />

                <div className="h-input-default flex items-center gap-4">
                    {button.image_url ? (<>
                        <ClickableImg src={button.image_url}
                                      alt={intl.formatMessage({ id: "b0052" })}
                                      height={24}
                                      width={24}
                        />
                        <button disabled={disabled || buttonType === "text"}
                                aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={onDeleteImage}
                        >
                            <DeleteIcon className="text-point-red size-4" />
                        </button>
                    </>) : (
                        <ImageUploadBtn disabled={disabled || buttonType === "text"}
                                        onUpload={onUploadImage}
                        />
                    )}
                </div>
            </div>

            <div role="group"
                 aria-label={intl.formatMessage({ id: "b0023" })}
                 className="mt-1 flex gap-1">
                <ParagraphButtonTypeDropdown width={120}
                                             disabled={disabled}
                                             button={button}
                                             options={BUTTON_TYPE_OPTIONS}
                                             onChange={onChange}
                />

                <ParagraphButtonInputs button={button}
                                       disabled={disabled}
                                       onChange={onChange}
                />
            </div>
        </div>
    );
};

export default ChatQuickMenuInputGroup;
